// src/components/AlbumGatheringLoader.jsx
// OPTIMIZED: Smoother animations with reduced jitter
// - Fewer concurrent flights
// - Better GPU acceleration
// - Smoother transition to completion

import React, { useState, useEffect, useMemo, useRef } from 'react';

// ============================================================
// CONFIGURATION - Optimized for smooth performance
// ============================================================
const CONFIG = {
  // Timing - Slower and smoother
  PHOTO_FLIGHT_DURATION: 4000,      // ms: Longer for smoother motion
  PHOTO_STAGGER_DELAY: 2200,        // ms: More gap between photos (less overlap)
  COMPLETION_DELAY: 4000,           // ms: Longer pause before transition
  MAX_CONCURRENT_FLIGHTS: 1,        // Limit concurrent animations
  
  // Visual
  BOOK_PERSPECTIVE: 1000,           
  BOOK_ROTATION: 20,                
  PHOTO_START_SCALE: 0.5,           
  PHOTO_HERO_SCALE: 1.15,           
  PHOTO_END_SCALE: 0.05,            
};

// ============================================================
// STATUS MESSAGES
// ============================================================
const STATUS_MESSAGES = [
  { threshold: 0, text: "Gathering your memories...", subtext: "Each photo tells a story" },
  { threshold: 20, text: "Curating the best shots...", subtext: "Finding the perfect moments" },
  { threshold: 40, text: "Organizing by location...", subtext: "Mapping your journey" },
  { threshold: 60, text: "Weaving the narrative...", subtext: "Connecting the days together" },
  { threshold: 80, text: "Polishing your album...", subtext: "Almost ready to view" },
  { threshold: 100, text: "Your story is ready!", subtext: "Opening album..." },
];

function getStatusForProgress(progress) {
  for (let i = STATUS_MESSAGES.length - 1; i >= 0; i--) {
    if (progress >= STATUS_MESSAGES[i].threshold) {
      return STATUS_MESSAGES[i];
    }
  }
  return STATUS_MESSAGES[0];
}

// ============================================================
// FLOATING PHOTO COMPONENT - Optimized for smooth animation
// ============================================================
function FloatingPhoto({ photo, positionIndex, isComplete }) {
  const [animationState, setAnimationState] = useState('waiting');
  
  // Stable starting position using positionIndex as seed for deterministic "randomness"
  const startPosition = useMemo(() => {
    const seed = positionIndex * 137.5; // Golden angle for distribution
    const side = positionIndex % 3;
    const variance = Math.sin(seed) * 25; // Deterministic variance
    
    switch (side) {
      case 0: // Left
        return { x: -55, y: -15 + variance, rotation: -10 + Math.cos(seed) * 6 };
      case 1: // Top
        return { x: variance * 0.7, y: -65, rotation: Math.sin(seed) * 12 };
      case 2: // Right
        return { x: 55, y: -15 + variance, rotation: 10 - Math.cos(seed) * 6 };
      default:
        return { x: 0, y: -60, rotation: 0 };
    }
  }, [positionIndex]);

  useEffect(() => {
    // Use double requestAnimationFrame for reliable CSS transition triggering
    let frameId1, frameId2, landTimer;
    
    frameId1 = requestAnimationFrame(() => {
      frameId2 = requestAnimationFrame(() => {
        setAnimationState('flying');
      });
    });

    // Mark as landed after flight completes (with small buffer)
    landTimer = setTimeout(() => {
      setAnimationState('landed');
    }, CONFIG.PHOTO_FLIGHT_DURATION + 100);
    
    return () => {
      cancelAnimationFrame(frameId1);
      cancelAnimationFrame(frameId2);
      clearTimeout(landTimer);
    };
  }, []);

  if (animationState === 'waiting') return null;
  if (animationState === 'landed' && !isComplete) return null;

  const photoUrl = photo?.url || photo?.src || photo;

  return (
    <div
      className={`floating-photo ${animationState}`}
      style={{
        '--start-x': `${startPosition.x}vw`,
        '--start-y': `${startPosition.y}vh`,
        '--start-rot': `${startPosition.rotation}deg`,
        '--flight-duration': `${CONFIG.PHOTO_FLIGHT_DURATION}ms`,
        '--hero-scale': CONFIG.PHOTO_HERO_SCALE,
        '--start-scale': CONFIG.PHOTO_START_SCALE,
      }}
    >
      <div className="photo-inner-frame">
        <img 
          src={photoUrl} 
          alt="" 
          draggable="false"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
    </div>
  );
}

// ============================================================
// 3D BOOK COMPONENT
// ============================================================
function Book3D({ isComplete, progress }) {
  const intensity = Math.min(progress / 100, 1);
  
  return (
    <div className="book-container">
      {/* Magic Portal Glow */}
      <div 
        className="book-magic-portal"
        style={{ 
          opacity: 0.2 + (intensity * 0.4),
          transform: `translate(-50%, -50%) scale(${1 + (intensity * 0.2)})`
        }}
      />
      
      {/* 3D Book */}
      <div className={`book-3d ${isComplete ? 'complete' : ''}`}>
        {/* Left page */}
        <div className="book-page book-page-left">
          <div className="page-content">
            <div className="page-header" />
            <div className="page-lines">
              <div className="line l1" />
              <div className="line l2" />
              <div className="line l3" />
            </div>
            <div className="page-photo-stack" style={{ opacity: intensity }}>
              {progress > 10 && <div className="mini-photo p1" />}
              {progress > 40 && <div className="mini-photo p2" />}
            </div>
          </div>
        </div>
        
        {/* Book spine */}
        <div className="book-spine" />
        
        {/* Right page */}
        <div className="book-page book-page-right">
          <div className="page-content">
             <div className="page-header" />
             <div className="page-lines">
              <div className="line l1" />
              <div className="line l2" />
            </div>
             <div className="page-photo-stack" style={{ opacity: intensity }}>
               {progress > 25 && <div className="mini-photo p3" />}
               {progress > 60 && <div className="mini-photo p4" />}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PARTICLE EFFECTS - Fewer, smoother particles
// ============================================================
function Particles({ intensity }) {
  const particles = useMemo(() => [...Array(8)].map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 3,
    delay: Math.random() * 6,
    dur: 6 + Math.random() * 4
  })), []);

  return (
    <div className="particles-container">
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            opacity: 0.2 + (intensity * 0.3)
          }}
        />
      ))}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function AlbumGatheringLoader({
  isVisible,
  photos = [],
  progress = 0,
  onComplete,
  onCancel,
}) {
  const [isExiting, setIsExiting] = useState(false);
  const completionTriggered = useRef(false);
  
  // Continuous flight state
  const [activeFlights, setActiveFlights] = useState([]);
  const flightIdCounter = useRef(0);

  // 1. Completion Logic - Smoother transition
  useEffect(() => {
    if (progress >= 100 && !completionTriggered.current && onComplete) {
      completionTriggered.current = true;
      
      // Longer delay for smooth exit
      setTimeout(() => {
        setIsExiting(true);
        // Wait for exit animation before calling complete
        setTimeout(onComplete, 800); 
      }, CONFIG.COMPLETION_DELAY);
    }
  }, [progress, onComplete]);

  // 2. Reset on visibility toggle
  useEffect(() => {
    if (isVisible) {
      setIsExiting(false);
      completionTriggered.current = false;
      setActiveFlights([]);
      flightIdCounter.current = 0;
    }
  }, [isVisible]);

  // 3. Continuous Photo Spawning Logic - Optimized for smoothness
  useEffect(() => {
    if (!isVisible || isExiting || !photos || photos.length === 0) return;
    
    const maxConcurrent = CONFIG.MAX_CONCURRENT_FLIGHTS;

    const spawnFlight = () => {
      setActiveFlights(prev => {
        // Count flights that are still animating
        const now = Date.now();
        const activeCount = prev.filter(f => {
          const age = now - (f.spawnTime || 0);
          return age < CONFIG.PHOTO_FLIGHT_DURATION;
        }).length;
        
        // Don't spawn if we're at max concurrent flights
        if (activeCount >= maxConcurrent) {
          return prev;
        }
        
        const idx = flightIdCounter.current;
        const photo = photos[idx % photos.length];
        const newFlight = { 
          id: idx, 
          photo, 
          positionIndex: idx,
          spawnTime: now
        };
        
        flightIdCounter.current += 1;
        
        // Clean up old flights (keep max 4 in DOM)
        const cleaned = prev.filter(f => {
          const age = now - (f.spawnTime || 0);
          return age < CONFIG.PHOTO_FLIGHT_DURATION + 500;
        });
        
        return [...cleaned, newFlight].slice(-4);
      });
    };

    // Initial spawn with slight delay for smoother start
    const initialTimer = setTimeout(spawnFlight, 300);
    
    // Loop with consistent interval
    const interval = setInterval(spawnFlight, CONFIG.PHOTO_STAGGER_DELAY);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isVisible, isExiting, photos]);

  const currentStatus = getStatusForProgress(progress);
  const isComplete = progress >= 100;

  if (!isVisible) return null;

  return (
    <>
      <style>{styles}</style>
      <div 
        className={`album-gathering-loader ${isExiting ? 'exiting' : ''}`}
        role="dialog"
      >
        <div className="loader-background">
          <div className="bg-gradient" />
          <Particles intensity={progress / 100} />
        </div>

        <div className="loader-content">
          <div className="loader-header">
            <h2 className="main-title">{currentStatus.text}</h2>
            <p className="sub-title">{currentStatus.subtext}</p>
          </div>

          <div className="book-scene">
            {/* The Book */}
            <Book3D 
              isComplete={isComplete} 
              progress={progress}
            />
            
            {/* The Photos Layer - Continuous Stream */}
            <div className="photos-layer">
              {activeFlights.map((flight) => (
                <FloatingPhoto
                  key={flight.id}
                  photo={flight.photo}
                  positionIndex={flight.positionIndex}
                  isComplete={isComplete}
                />
              ))}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-track">
              <div 
                className="progress-fill"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            {onCancel && progress < 90 && (
              <button className="cancel-text-btn" onClick={onCancel}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// STYLES - Optimized for GPU acceleration
// ============================================================
const styles = `
/* BASE CONTAINER */
.album-gathering-loader {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  animation: fadeIn 0.5s ease-out forwards;
}

.album-gathering-loader.exiting {
  animation: fadeOut 0.8s ease-in forwards;
}

@keyframes fadeIn { 
  from { opacity: 0; } 
  to { opacity: 1; } 
}

@keyframes fadeOut { 
  from { opacity: 1; transform: scale(1); } 
  to { opacity: 0; transform: scale(1.05); } 
}

/* BACKGROUND */
.loader-background {
  position: absolute;
  inset: 0;
  background: #0f172a;
  overflow: hidden;
}

.bg-gradient {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 50% 50%, rgba(30, 41, 59, 1) 0%, rgba(15, 23, 42, 1) 100%);
}

.particles-container { position: absolute; inset: 0; pointer-events: none; }
.particle {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: floatUp linear infinite;
  will-change: transform, opacity;
}
@keyframes floatUp {
  0% { transform: translateY(0) scale(1); opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translateY(-100px) scale(0); opacity: 0; }
}

/* CONTENT LAYOUT */
.loader-content {
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 4rem 2rem 6rem 2rem;
  box-sizing: border-box;
}

/* HEADER */
.loader-header {
  text-align: center;
  animation: slideDown 0.6s ease-out;
}
@keyframes slideDown { 
  from { transform: translateY(-20px); opacity: 0; } 
  to { transform: translateY(0); opacity: 1; } 
}

.main-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
}
.sub-title {
  color: #94a3b8;
  font-size: 0.95rem;
  margin: 0;
}

/* BOOK SCENE */
.book-scene {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
}

/* 3D BOOK */
.book-container {
  position: relative;
  width: 280px;
  height: 200px;
  transform-style: preserve-3d;
}

.book-magic-portal {
  position: absolute;
  top: 50%; left: 50%;
  width: 300px; height: 300px;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%);
  filter: blur(20px);
  z-index: -1;
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.book-3d {
  width: 100%; height: 100%;
  transform-style: preserve-3d;
  transform: rotateX(25deg);
  transition: transform 1s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.book-3d.complete {
  transform: rotateX(0deg) scale(1.1);
}

.book-page {
  position: absolute;
  top: 0;
  width: 140px; height: 200px;
  background: #f8fafc;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), inset 0 0 20px rgba(0,0,0,0.05);
  backface-visibility: hidden;
  border-radius: 2px;
}

.book-page-left {
  left: 0;
  transform-origin: right center;
  transform: rotateY(-15deg);
  border-radius: 4px 0 0 4px;
  border-right: 1px solid #cbd5e1;
}

.book-page-right {
  right: 0;
  transform-origin: left center;
  transform: rotateY(15deg);
  border-radius: 0 4px 4px 0;
  border-left: 1px solid #cbd5e1;
}

/* Page Details */
.page-content { padding: 16px; opacity: 0.8; }
.page-header { height: 12px; width: 60%; background: #e2e8f0; margin-bottom: 16px; border-radius: 2px; }
.line { height: 4px; background: #f1f5f9; margin-bottom: 6px; width: 100%; border-radius: 2px; }
.l1 { width: 90%; } .l2 { width: 85%; } .l3 { width: 40%; }

.page-photo-stack { position: relative; margin-top: 20px; height: 60px; transition: opacity 0.5s; }
.mini-photo {
  position: absolute;
  width: 50px; height: 40px;
  background: #cbd5e1;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.p1 { transform: rotate(-5deg); left: 10px; background: #94a3b8; }
.p2 { transform: rotate(3deg); left: 25px; top: 5px; background: #64748b; }
.p3 { transform: rotate(2deg); right: 10px; background: #94a3b8; }
.p4 { transform: rotate(-4deg); right: 25px; top: 8px; background: #64748b; }

.book-spine {
  position: absolute;
  left: 50%; top: 0;
  width: 24px; height: 200px;
  background: #78350f;
  transform: translateX(-50%) translateZ(-2px) rotateY(90deg);
}

/* =========================================
   THE HERO PHOTO ANIMATION - Optimized
   ========================================= */
.photos-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  perspective: 1000px;
}

.floating-photo {
  position: absolute;
  top: 50%; left: 50%;
  width: 140px;
  height: auto;
  transform: translate(-50%, -50%) translateZ(0);
  will-change: transform, opacity;
  z-index: 100;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000px;
  contain: layout style paint;
}

.photo-inner-frame {
  background: white;
  padding: 6px 6px 16px 6px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  border-radius: 2px;
  transform-origin: center center;
}

.floating-photo img {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  background: #e2e8f0;
}

/* THE ANIMATION - Smoother easing */
.floating-photo.flying {
  animation: cinematicFlyIn var(--flight-duration) cubic-bezier(0.33, 0, 0.2, 1) forwards;
}

@keyframes cinematicFlyIn {
  0% {
    opacity: 0;
    transform: 
      translate(calc(-50% + var(--start-x)), calc(-50% + var(--start-y)))
      scale(var(--start-scale))
      rotate(var(--start-rot))
      translateZ(0);
  }
  15% {
    opacity: 1;
  }
  /* THE HERO MOMENT */
  40% {
    opacity: 1;
    transform: 
      translate(-50%, -60%)
      scale(var(--hero-scale))
      rotate(0deg)
      translateZ(0);
    z-index: 200;
  }
  52% {
     transform: 
      translate(-50%, -60%)
      scale(var(--hero-scale))
      rotate(0deg)
      translateZ(0);
  }
  /* THE SUCK IN */
  100% {
    opacity: 0;
    transform: 
      translate(-50%, -40%)
      scale(0.1)
      rotate(8deg)
      translateZ(0);
  }
}

/* PROGRESS BAR */
.progress-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.progress-track {
  width: 100%;
  height: 4px;
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #4ade80;
  box-shadow: 0 0 10px #4ade80;
  transition: width 0.4s ease-out;
}

.cancel-text-btn {
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s;
}
.cancel-text-btn:hover { color: #cbd5e1; }

/* MOBILE ADJUSTMENTS */
@media (max-height: 700px) {
  .loader-content { padding: 2rem 1rem 4rem 1rem; }
  .book-scene { align-items: flex-end; padding-bottom: 20px; }
  .book-container { transform: scale(0.85); }
}

/* Reduce motion for users who prefer it */
@media (prefers-reduced-motion: reduce) {
  .floating-photo.flying {
    animation-duration: 0.5s;
  }
  .particle {
    animation: none;
  }
}
`;